import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Trophy, Clock, Ticket } from "lucide-react";

const META = {
  product: {
    label: "Product Widget",
    desc: "Displayed beneath the Add to Cart button on every product page.",
    fields: [
      { key: "enabled", type: "switch", label: "Enable widget" },
      { key: "heading", type: "text", label: "Heading text" },
      { key: "show_giveaway_title", type: "switch", label: "Show giveaway title" },
      { key: "countdown", type: "switch", label: "Show countdown timer" },
      { key: "position", type: "select", label: "Position", options: [["below", "Below Add to Cart"], ["above", "Above Add to Cart"]] },
      { key: "show_entries_earned", type: "switch", label: "Show entries earned" },
      { key: "show_ratio", type: "switch", label: 'Show "Every $1 = X Entries"' },
      { key: "accent_color", type: "color", label: "Accent color" },
    ],
  },
  cart: {
    label: "Cart Widget",
    desc: "Live entry calculator shown on the cart page.",
    fields: [
      { key: "enabled", type: "switch", label: "Enable widget" },
      { key: "heading", type: "text", label: "Heading text" },
      { key: "show_entries_earned", type: "switch", label: "Show potential entries earned" },
      { key: "countdown", type: "switch", label: "Show countdown timer" },
      { key: "accent_color", type: "color", label: "Accent color" },
    ],
  },
  checkout: {
    label: "Checkout Widget",
    desc: "Shown on the Thank You page after an order is placed.",
    fields: [
      { key: "enabled", type: "switch", label: "Enable widget" },
      { key: "thank_you_message", type: "text", label: "Thank you message" },
      { key: "show_entries_earned", type: "switch", label: "Show entries earned" },
      { key: "prize_image", type: "text", label: "Prize image URL" },
      { key: "confetti", type: "switch", label: "Confetti animation" },
      { key: "accent_color", type: "color", label: "Accent color" },
    ],
  },
};

export default function WidgetSettings() {
  const { widget } = useParams();
  const navigate = useNavigate();
  const meta = META[widget];
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!meta) { navigate("/widgets"); return; }
    api.get("/widgets/settings").then((r) => setCfg(r.data[widget])).catch(() => toast.error("Failed to load"));
  }, [widget]);

  const set = (key, val) => setCfg((p) => ({ ...p, [key]: val }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/widgets/settings", { [widget]: cfg });
      toast.success("Settings saved");
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (!meta || !cfg) {
    return <Layout><div className="max-w-3xl mx-auto px-8 py-10 text-sm text-zinc-500">Loading…</div></Layout>;
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-8 py-10" data-testid={`widget-settings-${widget}`}>
        <Link to="/widgets" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-950 mb-4" data-testid="back-to-widgets">
          <ArrowLeft className="size-4" /> Back to widgets
        </Link>
        <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-950">{meta.label}</h1>
        <p className="text-sm text-zinc-500 mt-1">{meta.desc}</p>

        <div className="grid md:grid-cols-2 gap-6 mt-8">
          {/* Settings form */}
          <div className="bg-white border border-zinc-200 rounded-lg p-5 space-y-5">
            {meta.fields.map((f) => (
              <div key={f.key} className="flex items-center justify-between gap-4">
                <Label className="text-sm text-zinc-700">{f.label}</Label>
                {f.type === "switch" && (
                  <Switch checked={!!cfg[f.key]} onCheckedChange={(v) => set(f.key, v)} data-testid={`field-${f.key}`} />
                )}
                {f.type === "text" && (
                  <Input value={cfg[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} className="max-w-[220px]" data-testid={`field-${f.key}`} />
                )}
                {f.type === "color" && (
                  <input type="color" value={cfg[f.key] || "#10B981"} onChange={(e) => set(f.key, e.target.value)} className="size-9 rounded border border-zinc-200 cursor-pointer" data-testid={`field-${f.key}`} />
                )}
                {f.type === "select" && (
                  <Select value={cfg[f.key]} onValueChange={(v) => set(f.key, v)}>
                    <SelectTrigger className="max-w-[220px]" data-testid={`field-${f.key}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {f.options.map(([val, lbl]) => <SelectItem key={val} value={val}>{lbl}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
            ))}
            <Button onClick={save} disabled={saving} className="w-full" data-testid="save-widget-settings">
              {saving ? "Saving…" : "Save settings"}
            </Button>
          </div>

          {/* Live preview */}
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-2">Preview</div>
            <WidgetPreview widget={widget} cfg={cfg} />
          </div>
        </div>
      </div>
    </Layout>
  );
}

function WidgetPreview({ widget, cfg }) {
  const accent = cfg.accent_color || "#10B981";
  const exampleEntries = 300;
  if (widget === "product") {
    return (
      <div className="rounded-lg border-2 p-4 bg-white" style={{ borderColor: accent }} data-testid="widget-preview">
        {cfg.show_giveaway_title && <div className="flex items-center gap-1.5 text-xs font-semibold mb-1" style={{ color: accent }}><Trophy className="size-3.5" /> 2026 Yamaha YFZ450R Giveaway</div>}
        <div className="text-xs text-zinc-500">{cfg.heading || "This purchase earns"}</div>
        {cfg.show_entries_earned && <div className="font-display text-2xl font-bold" style={{ color: accent }}>{exampleEntries.toLocaleString()} Giveaway Entries</div>}
        {cfg.show_ratio && <div className="text-[11px] text-zinc-500 mt-1">Every $1 = 10 entries</div>}
        {cfg.countdown && <div className="flex items-center gap-1 text-[11px] text-zinc-500 mt-2"><Clock className="size-3" /> Ends in 12d 6h 41m</div>}
      </div>
    );
  }
  if (widget === "cart") {
    return (
      <div className="rounded-lg border-2 p-4 bg-white" style={{ borderColor: accent }} data-testid="widget-preview">
        <div className="flex items-center gap-1.5 text-xs font-semibold mb-1" style={{ color: accent }}><Ticket className="size-3.5" /> {cfg.heading || "Your entries so far"}</div>
        {cfg.show_entries_earned && <div className="font-display text-2xl font-bold" style={{ color: accent }}>1,250 entries</div>}
        <div className="text-[11px] text-zinc-500 mt-1">Add more to your cart to earn more entries.</div>
        {cfg.countdown && <div className="flex items-center gap-1 text-[11px] text-zinc-500 mt-2"><Clock className="size-3" /> Ends in 12d 6h 41m</div>}
      </div>
    );
  }
  return (
    <div className="rounded-lg border-2 p-5 bg-white text-center" style={{ borderColor: accent }} data-testid="widget-preview">
      {cfg.prize_image ? <img src={cfg.prize_image} alt="" className="w-full h-28 object-cover rounded-md mb-3" /> : <div className="w-full h-28 bg-zinc-100 rounded-md mb-3 flex items-center justify-center text-zinc-400 text-xs">Prize image</div>}
      <div className="font-display text-lg font-bold text-zinc-950">{cfg.thank_you_message || "🎉 Thanks for your order!"}</div>
      <div className="text-xs text-zinc-500 mt-1">You earned</div>
      {cfg.show_entries_earned && <div className="font-display text-3xl font-bold my-1" style={{ color: accent }}>300 Giveaway Entries</div>}
      <div className="text-[11px] text-zinc-500">Your entries have been added automatically.</div>
      <div className="text-xs font-medium text-zinc-950 mt-2">Current Giveaway: 2026 Yamaha YFZ450R</div>
      {cfg.confetti && <div className="text-[11px] mt-1" style={{ color: accent }}>🎊 Confetti enabled</div>}
    </div>
  );
}
